import { useRef, useEffect } from "react";
import type { SlashCommandResult, SlashCommandType } from "../types/index.js";

interface SlashCommandDropdownProps {
	results: SlashCommandResult[];
	activeIndex: number;
	isLoading: boolean;
	activeType: SlashCommandType | null;
	onSelect: (result: SlashCommandResult) => void;
}

/** SVG icon for user entity results. */
function UserIcon() {
	return (
		<svg
			className="slash-dropdown-icon"
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			aria-hidden="true"
		>
			<circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
			<path
				d="M2.5 14c0-3 2.5-4.5 5.5-4.5s5.5 1.5 5.5 4.5"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				fill="none"
			/>
		</svg>
	);
}

/** SVG icon for task entity results. */
function TaskIcon() {
	return (
		<svg
			className="slash-dropdown-icon"
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			aria-hidden="true"
		>
			<rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
			<path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
		</svg>
	);
}

/** SVG flag icon for milestone entity results. */
function MilestoneIcon() {
	return (
		<svg
			className="slash-dropdown-icon"
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			aria-hidden="true"
		>
			<line x1="4" y1="2" x2="4" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
			<path d="M4 3h8l-2 2.5 2 2.5H4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none" />
		</svg>
	);
}

/** SVG dot icon for status entity results. */
function StatusIcon() {
	return (
		<svg
			className="slash-dropdown-icon"
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			aria-hidden="true"
		>
			<circle cx="8" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
			<circle cx="8" cy="8" r="2" fill="currentColor" />
		</svg>
	);
}

/** Returns the human-readable section label for a given entity type. */
function getTypeLabel(type: SlashCommandType | null): string {
	switch (type) {
		case "user": return "Users";
		case "task": return "Tasks";
		case "milestone": return "Milestones";
		case "status": return "Statuses";
		default: return "Results";
	}
}

/** Returns the icon component for a given entity type. */
function EntityIcon({ type }: { type: SlashCommandType }) {
	switch (type) {
		case "user": return <UserIcon />;
		case "milestone": return <MilestoneIcon />;
		case "status": return <StatusIcon />;
		case "task":
		default: return <TaskIcon />;
	}
}

/**
 * Autocomplete dropdown for slash command results.
 *
 * Positioned above the chat input (opens upward) and supports
 * keyboard navigation via arrow keys. Handles user, task, milestone,
 * and status entity types.
 */
export default function SlashCommandDropdown({
	results,
	activeIndex,
	isLoading,
	activeType,
	onSelect,
}: SlashCommandDropdownProps) {
	const listRef = useRef<HTMLUListElement>(null);
	const activeItemRef = useRef<HTMLLIElement>(null);

	// Scroll the active item into view when navigating with keyboard
	useEffect(() => {
		activeItemRef.current?.scrollIntoView({ block: "nearest" });
	}, [activeIndex]);

	const typeLabel = getTypeLabel(activeType);

	return (
		<div id="slash-command-listbox" className="slash-dropdown" role="listbox" aria-label={`Search ${typeLabel}`}>
			{isLoading && results.length === 0 && (
				<div className="slash-dropdown-status">
					<span className="slash-dropdown-spinner" aria-hidden="true" />
					Searching {typeLabel}...
				</div>
			)}

			{!isLoading && results.length === 0 && (
				<div className="slash-dropdown-status">
					No {typeLabel.toLowerCase()} found
				</div>
			)}

			{results.length > 0 && (
				<ul ref={listRef} className="slash-dropdown-list">
					{results.map((result, index) => (
						<li
							key={`${result.type}-${result.id}`}
							ref={index === activeIndex ? activeItemRef : undefined}
							className={`slash-dropdown-item slash-dropdown-item--${result.type}${index === activeIndex ? " slash-dropdown-item--active" : ""}`}
							role="option"
							aria-selected={index === activeIndex}
							onMouseDown={(e) => {
								// Prevent the input from losing focus
								e.preventDefault();
								onSelect(result);
							}}
						>
							<span className="slash-dropdown-item-icon">
								<EntityIcon type={result.type} />
							</span>
							<span className="slash-dropdown-item-content">
								<span className="slash-dropdown-item-label">{result.label}</span>
								{result.meta && (
									<span className="slash-dropdown-item-meta">{result.meta}</span>
								)}
							</span>
						</li>
					))}
				</ul>
			)}

			<div className="slash-dropdown-hint">
				<kbd>↑</kbd> <kbd>↓</kbd> navigate &middot; <kbd>Enter</kbd> select &middot; <kbd>Esc</kbd> close
			</div>
		</div>
	);
}
